import debounce from 'lodash.debounce'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { calculateArea, getSpread } from '../util/calc'
import Button from './Button'
import IconButton from './IconButton'
import MinusIcon from './icons/minus'
import PlusIcon from './icons/plus'
import { useQuery } from '@apollo/client'
import { GetOrder } from '../graphql/queries'

const rowHeaders = [
    'PCODE',
    'BOX',
    'MATERIAL',
    'L',
    'B',
    'H',
    'H_EXTRA',
    'AMOUNT',
    'FPRICE',
    'PRINT',
    'PRODUCTION_CODE',
    'DENSITY',
    'AREA',
]

let init = false

const OrderItemsList = ({
    orderId,
    onRowSelect,
    onSendProduction,
    onAddRow,
    onDuplicateRow,
    onDeleteRow,
    initialRow,
}) => {
    const { t } = useTranslation()
    const [selectedRow, setSelectedRow] = useState()
    const [items, setItems] = useState([])
    const tableRef = useRef()

    useQuery(GetOrder, {
        variables: {
          id: parseInt(orderId),
        },
        onCompleted: (data) => {
            const { getOrder: order } = data
            if (!order) return

            setItems(order.orderRows || [])
        },
    })

    useEffect(() => {
        return () => {
            init = false
            setSelectedRow(null)
        }
    }, [])

    useEffect(() => {
        setLastPosition()
    }, [items.length])

    useEffect(() => {
        window.addEventListener('keydown', handleArrowKey)

        return () => {
            window.removeEventListener('keydown', handleArrowKey)
        }
    }, [selectedRow])

    const setLastPosition = () => {
        if (!init && initialRow) {
            init = true
            const item = items.find(x => x.ID === initialRow)
            handleRowSelect(item)
            return
        } else if (items && items.length > 0) {
            init = true
            const item = items[items.length - 1]
            handleRowSelect(item)
            return
        }
        init = true
        handleRowSelect(null)
        setTimeout(() => {
            tableRef.current?.querySelector(`.order-row--selected`)?.scrollIntoView(false)
        }, 1000)
    }

    const handleRowSelect = (row) => {
        setSelectedRow(row)
        if (onRowSelect) onRowSelect(row)
    }

    const getRowColumnValue = (row, field) => {
        if (field === 'PCODE') {
            return `P-${row.ID || '0'}-${row.CREATION_YEAR || '0'}${row.FSC ? '-F' : ''}`
        }
        if (field === 'AREA') {
            const spread = getSpread(row)
            const area = (calculateArea(spread.length, spread.width, parseInt(row?.PER_SHEET))).toFixed(4)
            return area ? area : '0'
        }
        if (field === 'FPRICE') {
            const price = row?.FPRICE || 0
            const transport = row?.TRANSPORT || 0

            return parseFloat(price + transport).toFixed(3)
        }
        return row[field]
    }

    const handleDuplicate = async () => {
        if (!onDuplicateRow) return
        const selectedIndex = items.findIndex(x => x.ID === selectedRow?.ID)
        const newRow = await onDuplicateRow(items[selectedIndex])
        if (newRow) handleRowSelect(newRow)
    }

    const handleDelete = async () => {
        if (!onDeleteRow) return
        await onDeleteRow(selectedRow)
    }

    const handleAdd = async () => {
        if (!onAddRow) return
        const newRow = await onAddRow()
        if (newRow) handleRowSelect(newRow)
    }

    const handleProduction = () => {
        if (!onSendProduction) return
        onSendProduction()
    }

    const handleArrowKey = (e) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault()
        
            const currentIndex = items.findIndex(x => x.ID === selectedRow?.ID)
            let row = null

            const index = currentIndex - 1
            if (index <= items.length - 1 && index >= 0) {
                row = items[index]
                handleRowSelect(row)
            }
            tableRef.current?.querySelector(`.order-row--selected`)?.scrollIntoView(false)
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault()
        
            const currentIndex = items.findIndex(x => x.ID === selectedRow?.ID)
            let row = null
            
            const index = currentIndex + 1
            if (index <= items.length - 1 && index >= 0) {
                row = items[index]
                handleRowSelect(row)
            }
            tableRef.current?.querySelector(`.order-row--selected`)?.scrollIntoView(false)
        }
    }

    const handleMwheel = (e) => {
        const delta = Math.sign(e.deltaY)
        const currentIndex = items.findIndex(x => x.ID === selectedRow?.ID)

        let row = null
        

        if (delta <= 0) {
            const index = currentIndex - 1
            if (index <= items.length - 1 && index >= 0) {
                row = items[index]
                handleRowSelect(row)
            }
        }
        if (delta > 0) {
            const index = currentIndex + 1
            if (index <= items.length - 1 && index >= 0) {
                row = items[index]
                handleRowSelect(row)
            }
        }

        tableRef.current?.querySelector(`.order-row--selected`)?.scrollIntoView(false)
    }

    const debouceMwheel = useMemo( () => debounce(handleMwheel, 50), [selectedRow])

    return (
        <div className='table-container'>
            <div className='table-controls'>
                <div className='field-row'>
                    <IconButton
                        label={'Lisa'}
                        icon={<PlusIcon />}
                        onClick={handleAdd}
                    />
                    <IconButton
                        label={'Kustuta'}
                        icon={<MinusIcon />}
                        disabled={!selectedRow}
                        onClick={handleDelete}
                    />
                </div>
                <Button
                    label={t('Tootmisesse')}
                    disabled={items?.length < 0}
                    onClick={handleProduction}
                />

                <Button
                    label={'Kopeeri'}
                    onClick={handleDuplicate}
                    disabled={!selectedRow}
                />
            </div>
            { items?.length > 0 ?
                <div ref={tableRef} className='table-wrapper'>
                    <table className='order-rows'>
                        <thead
                            className='order-rows--header'
                            style={{
                                gridTemplateColumns: `repeat(${rowHeaders.length}, 1fr)`
                            }}
                        >
                            <tr>
                            {
                                rowHeaders.map((headerItem) =>
                                    <th key={`header-${headerItem}`} className='order-rows--header-item'>
                                        { t(headerItem) }
                                    </th>
                                )
                            }
                            </tr>
                        </thead>
                        <tbody className='order-rows--content'>
                            {
                                items.map((row, index) =>
                                    <tr
                                        onClick={(e) => {
                                            if (e.target.classList.contains('btn')) return
                                            handleRowSelect(row)
                                        }}
                                        key={`order-row-${index}`}
                                        className={`order-row${selectedRow?.ID === row.ID ? ' order-row--selected' : ''}`}
                                        style={{
                                            gridTemplateColumns: `repeat(${rowHeaders.length}, 1fr)`
                                        }}
                                        data-id={row.ID}
                                    >
                                        {
                                            rowHeaders.map((headerItem, itemIndex) =>
                                                <td
                                                    key={`order-row-${index}-${itemIndex}`}
                                                    className='order-row--item'
                                                >
                                                    { getRowColumnValue(row, headerItem) }
                                                </td>
                                            )
                                        }
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>
                </div>
                :
                <div className='empty-result'>
                    { t('Tellimuse read puuduvad') }
                </div>
            }
        </div>
    )
}

export default OrderItemsList